/**
 * Supabase会社関連サービス
 * 会社データ、会社案件、会社設定の管理を担当
 */

import { SupabaseClient } from '@supabase/supabase-js';
import logger from '../../utils/logger';
import { getSupabaseClient } from '../../utils/supabase';
import {
  Company,
  CompanyCase,
  CompanyPartnerSettings,
  CompanyPartnerSelection,
  CompanyVehicleModelSettings,
  DriverTransportFeeVisibilitySettings,
  DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS,
} from '../../types';
import { DataServiceError, ErrorCodes } from '../dataService';

export class SupabaseCompanyService {
  public supabase: SupabaseClient | null = null;
  public currentCompanyId: string;

  constructor() {
    this.initializeSupabase();
    this.currentCompanyId = 'e32a0499-6456-44ac-9b16-b0001098c259'; // デフォルト会社ID
  }

  private initializeSupabase() {
    try {
      this.supabase = getSupabaseClient();
      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }
      logger.info('✅ SupabaseCompanyService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ SupabaseCompanyService initialization failed:', error);
    }
  }

  private ensureSupabaseClient() {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
  }

  // =============================================================================
  // Company関連メソッド
  // =============================================================================

  /**
   * 全ての会社を取得
   * @returns 会社一覧
   */
  async getCompanies(): Promise<Company[]> {
    logger.info('🏢 SupabaseCompanyService: getCompanies開始');
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('companies').select('*').order('name');

      if (error) {
        throw new DataServiceError(
          `会社データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const companies = (data || []).map((company: any) => ({
        id: company.id,
        name: company.name,
        code: company.code || company.id,
        address: company.address,
        phone: company.phone,
        email: company.email,
        contactPerson: company.contact_person,
        // FL-184: statusカラムは削除され、subscriptionStatusに統合
        subscriptionPlan: company.subscription_plan || 'basic',
        subscriptionStatus: company.subscription_status || 'active',
        maxUsers: company.max_users || 10,
        maxDrivers: company.max_drivers || 5,
        maxVehicles: company.max_vehicles || 10,
        settings: company.settings || {},
        createdAt: company.created_at,
        updatedAt: company.updated_at,
      }));

      logger.info(`✅ SupabaseCompanyService: getCompanies完了 - ${companies.length}件取得`);
      return companies;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanies失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '会社データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社設定を更新
   * @param companyId 会社ID
   * @param updateData 更新データ
   */
  async updateCompany(
    companyId: string,
    updateData: Partial<Pick<Company, 'driverScheduleVisibility' | 'driverScheduleEditPermission'>>
  ): Promise<void> {
    logger.info(`🔧 SupabaseCompanyService: updateCompany開始 - companyId: ${companyId}`);
    this.ensureSupabaseClient();

    try {
      const updatePayload: Record<string, any> = {
        updated_at: new Date().toISOString(),
      };

      // FL-114: スケジュール可視性設定
      if (updateData.driverScheduleVisibility !== undefined) {
        updatePayload.driver_schedule_visibility = updateData.driverScheduleVisibility;
      }

      // FL-115: スケジュール編集権限設定
      if (updateData.driverScheduleEditPermission !== undefined) {
        updatePayload.driver_schedule_edit_permission = updateData.driverScheduleEditPermission;
      }

      const { error } = await this.supabase!.from('companies')
        .update(updatePayload)
        .eq('id', companyId);

      if (error) {
        throw new DataServiceError(
          `会社設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseCompanyService: updateCompany完了`);
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: updateCompany失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '会社設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 会社の詳細情報を取得
   * @param companyId 会社ID
   * @returns 会社情報またはnull
   */
  async getCompanyById(companyId: string): Promise<Company | null> {
    logger.info(`🏢 SupabaseCompanyService: getCompanyById開始 - companyId: ${companyId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `会社情報の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      const company: Company = {
        id: data.id,
        name: data.name,
        code: data.code || data.id,
        address: data.address,
        phone: data.phone,
        email: data.email,
        contactPerson: data.contact_person,
        subscriptionPlan: data.subscription_plan || 'basic',
        subscriptionStatus: data.subscription_status || 'active',
        maxUsers: data.max_users || 10,
        maxDrivers: data.max_drivers || 5,
        maxVehicles: data.max_vehicles || 10,
        settings: data.settings || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        driverScheduleVisibility: data.driver_schedule_visibility || 'admin_only',
        driverScheduleEditPermission: data.driver_schedule_edit_permission || 'admin_only',
      };

      logger.info(`✅ SupabaseCompanyService: getCompanyById完了`);
      return company;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanyById失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '会社情報の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 陸送員のスケジュール編集権限をチェック
   * @param companyId 会社ID
   * @param userId ユーザーID
   * @param transporterId 陸送員ID（自分のスケジュールをチェックする場合）
   * @returns 編集権限があるかどうか
   */
  async checkDriverScheduleEditPermission(
    companyId: string,
    userId: string,
    transporterId?: string
  ): Promise<boolean> {
    // 入力パラメータのバリデーション
    if (!companyId || !userId) {
      logger.warn('必須パラメータが不足しています', { companyId, userId });
      return false;
    }

    logger.info(
      `🔐 SupabaseCompanyService: checkDriverScheduleEditPermission開始 - companyId: ${companyId}, userId: ${userId}, transporterId: ${transporterId}`
    );
    this.ensureSupabaseClient();

    try {
      // ユーザーの役割を取得
      const { data: userProfile, error: userError } = await this.supabase!.from('user_profiles')
        .select('role, company_id')
        .eq('id', userId)
        .single();

      if (userError || !userProfile) {
        logger.warn('ユーザープロファイルが見つかりません');
        return false;
      }

      // 管理者・配車管理者は常に編集可能
      if (userProfile.role === 'admin' || userProfile.role === 'dispatcher') {
        return true;
      }

      // 陸送員の場合、会社設定をチェック
      if (userProfile.role === 'driver') {
        const company = await this.getCompanyById(companyId);
        if (!company) {
          return false;
        }

        // admin_onlyの場合、陸送員は編集不可
        if (company.driverScheduleEditPermission === 'admin_only') {
          return false;
        }

        // driver_self_editの場合、自分のスケジュールのみ編集可能
        if (company.driverScheduleEditPermission === 'driver_self_edit' && transporterId) {
          // 陸送員IDとユーザーIDの関連をチェック
          const { data: transporterData, error: transporterError } = await this.supabase!.from(
            'company_land_transporters'
          )
            .select('user_id')
            .eq('id', transporterId)
            .eq('company_id', companyId)
            .single();

          if (transporterError || !transporterData) {
            return false;
          }

          return transporterData.user_id === userId;
        }
      }

      return false;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: checkDriverScheduleEditPermission失敗:', error);
      return false;
    }
  }

  /**
   * 指定した会社の案件を取得
   * @param companyId 会社ID（省略時はcurrentCompanyIdを使用）
   * @returns 案件一覧
   */
  async getCompanyCases(companyId?: string): Promise<CompanyCase[]> {
    const targetCompanyId = companyId || this.currentCompanyId;
    logger.info(`📋 SupabaseCompanyService: getCompanyCases開始 - companyId: ${targetCompanyId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_cases')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `案件データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const cases = (data || []).map((caseData: any) => ({
        id: caseData.id,
        companyId: caseData.company_id,
        status: caseData.status,
        priority: caseData.priority || 1,
        pickupAddress: caseData.pickup_address,
        pickupDate: caseData.pickup_date || new Date().toISOString().split('T')[0],
        deliveryAddress: caseData.delivery_address,
        deliveryDate: caseData.delivery_date || new Date().toISOString().split('T')[0],
        vehiclePlateNumberNone: caseData.vehicle_plate_number_none || false,
        createdAt: caseData.created_at,
        updatedAt: caseData.updated_at,
      }));

      logger.info(`✅ SupabaseCompanyService: getCompanyCases完了 - ${cases.length}件取得`);
      return cases;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanyCases失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '案件データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  // =============================================================================
  // CompanyPartnerSettings関連メソッド
  // =============================================================================

  /**
   * 会社の取引先設定を取得
   * @param companyId 会社ID
   * @returns 取引先設定またはnull
   */
  async getCompanyPartnerSettings(companyId: string): Promise<CompanyPartnerSettings | null> {
    logger.info(
      `🔧 SupabaseCompanyService: getCompanyPartnerSettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_partner_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `取引先設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      const settings: CompanyPartnerSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledPartnerIds: data.enabled_partner_ids || [],
        dataSourcePreferences: data.data_source_preferences || {},
        categoryPreferences: data.category_preferences || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseCompanyService: getCompanyPartnerSettings完了`);
      return settings;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanyPartnerSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社の取引先設定を更新
   * @param companyId 会社ID
   * @param updateData 更新データ
   * @returns 更新された設定
   */
  async updateCompanyPartnerSettings(
    companyId: string,
    updateData: Partial<
      Omit<CompanyPartnerSettings, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>
    >
  ): Promise<CompanyPartnerSettings> {
    logger.info(
      `🔧 SupabaseCompanyService: updateCompanyPartnerSettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_partner_settings')
        .update({
          enabled_partner_ids: updateData.enabledPartnerIds,
          data_source_preferences: updateData.dataSourcePreferences,
          category_preferences: updateData.categoryPreferences,
          updated_at: new Date().toISOString(),
        })
        .eq('company_id', companyId)
        .select('*')
        .single();

      if (error) {
        throw new DataServiceError(
          `取引先設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      const updatedSettings: CompanyPartnerSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledPartnerIds: data.enabled_partner_ids || [],
        dataSourcePreferences: data.data_source_preferences || {},
        categoryPreferences: data.category_preferences || {},
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseCompanyService: updateCompanyPartnerSettings完了`);
      return updatedSettings;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: updateCompanyPartnerSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // CompanyPartnerSelection関連メソッド
  // =============================================================================

  /**
   * 会社の取引先選択を取得
   * @param companyId 会社ID
   * @returns 取引先選択一覧
   */
  async getCompanyPartnerSelections(companyId: string): Promise<CompanyPartnerSelection[]> {
    logger.info(
      `🎯 SupabaseCompanyService: getCompanyPartnerSelections開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_partner_selections')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at');

      if (error) {
        throw new DataServiceError(
          `取引先選択の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const selections = (data || []).map((selection: any) => ({
        id: selection.id,
        companyId: selection.company_id,
        businessPartnerId: selection.business_partner_id || selection.partner_id,
        isEnabled: selection.is_enabled,
        roles: selection.roles || ['billing'],
        priority: selection.priority || 1,
        billingAllowed: selection.billing_allowed || true,
        pickupAllowed: selection.pickup_allowed || true,
        deliveryAllowed: selection.delivery_allowed || true,
        usageCount: selection.usage_count || selection.usage || 0,
        lastUsedAt: selection.last_used_at,
        createdAt: selection.created_at,
        updatedAt: selection.updated_at,
      }));

      logger.info(
        `✅ SupabaseCompanyService: getCompanyPartnerSelections完了 - ${selections.length}件取得`
      );
      return selections;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanyPartnerSelections失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい取引先選択を追加
   * @param selectionData 選択データ（IDなし）
   */
  async addCompanyPartnerSelection(
    selectionData: Omit<CompanyPartnerSelection, 'id'>
  ): Promise<void> {
    logger.info('🚀 SupabaseCompanyService: addCompanyPartnerSelection開始');
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_partner_selections').insert({
        company_id: selectionData.companyId,
        business_partner_id: selectionData.businessPartnerId,
        is_enabled: selectionData.isEnabled,
        roles: selectionData.roles,
        priority: selectionData.priority,
        billing_allowed: selectionData.billingAllowed,
        pickup_allowed: selectionData.pickupAllowed,
        delivery_allowed: selectionData.deliveryAllowed,
        usage_count: selectionData.usageCount,
        last_used_at: selectionData.lastUsedAt,
        created_at: selectionData.createdAt,
        updated_at: selectionData.updatedAt,
      });

      if (error) {
        throw new DataServiceError(
          `取引先選択の追加に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      logger.info('✅ SupabaseCompanyService: addCompanyPartnerSelection完了');
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: addCompanyPartnerSelection失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択の追加に失敗しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 取引先選択を更新
   * @param selectionId 選択ID
   * @param updateData 更新データ
   */
  async updateCompanyPartnerSelection(
    selectionId: string,
    updateData: Partial<
      Omit<CompanyPartnerSelection, 'id' | 'companyId' | 'businessPartnerId' | 'createdAt'>
    >
  ): Promise<void> {
    logger.info(
      `🔧 SupabaseCompanyService: updateCompanyPartnerSelection開始 - ID: ${selectionId}`
    );
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_partner_selections')
        .update({
          is_enabled: updateData.isEnabled,
          roles: updateData.roles,
          priority: updateData.priority,
          billing_allowed: updateData.billingAllowed,
          pickup_allowed: updateData.pickupAllowed,
          delivery_allowed: updateData.deliveryAllowed,
          usage_count: updateData.usageCount,
          last_used_at: updateData.lastUsedAt,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectionId);

      if (error) {
        throw new DataServiceError(
          `取引先選択の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      logger.info('✅ SupabaseCompanyService: updateCompanyPartnerSelection完了');
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: updateCompanyPartnerSelection失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 取引先選択を削除
   * @param selectionId 選択ID
   */
  async removeCompanyPartnerSelection(selectionId: string): Promise<void> {
    logger.info(
      `🗑️ SupabaseCompanyService: removeCompanyPartnerSelection開始 - ID: ${selectionId}`
    );
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_partner_selections')
        .delete()
        .eq('id', selectionId);

      if (error) {
        throw new DataServiceError(
          `取引先選択の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info('✅ SupabaseCompanyService: removeCompanyPartnerSelection完了');
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: removeCompanyPartnerSelection失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '取引先選択の削除に失敗しました',
        ErrorCodes.DATA_DELETION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // CompanyVehicleModelSettings関連メソッド
  // =============================================================================

  /**
   * 会社の車両モデル設定を取得
   * @param companyId 会社ID
   * @returns 車両モデル設定またはnull
   */
  async getCompanyVehicleModelSettings(
    companyId: string
  ): Promise<CompanyVehicleModelSettings | null> {
    logger.info(
      `🚗 SupabaseCompanyService: getCompanyVehicleModelSettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_vehicle_model_settings')
        .select('*')
        .eq('company_id', companyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // Not found
          return null;
        }
        throw new DataServiceError(
          `車両モデル設定の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        return null;
      }

      const settings: CompanyVehicleModelSettings = {
        id: data.id,
        companyId: data.company_id,
        enabledModelIds: data.enabled_model_ids || [],
        dataSourcePreferences: data.data_source_preferences || {
          allowRakuhaiProvided: true,
          allowCustom: true,
        },
        countryPreferences: data.country_preferences || {
          allowDomestic: true,
          allowForeign: false,
        },
        powertrainPreferences: data.powertrain_preferences || {
          allowGasoline: true,
          allowDiesel: true,
          allowHybrid: true,
          allowElectric: true,
          allowOther: true,
        },
        tagPreferences: data.tag_preferences || {
          allowImmobilized: true,
          allowLowHeight: true,
          allowWideBody: true,
          allowWelfare: true,
          allowCamping: true,
          allowClassic: true,
        },
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      logger.info(`✅ SupabaseCompanyService: getCompanyVehicleModelSettings完了`);
      return settings;
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: getCompanyVehicleModelSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '車両モデル設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 会社の車両モデル設定を更新
   * @param companyId 会社ID
   * @param updateData 更新データ
   */
  async updateCompanyVehicleModelSettings(
    companyId: string,
    updateData: Partial<
      Omit<CompanyVehicleModelSettings, 'id' | 'companyId' | 'createdAt' | 'updatedAt'>
    >
  ): Promise<void> {
    logger.info(
      `🚗 SupabaseCompanyService: updateCompanyVehicleModelSettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_vehicle_model_settings')
        .update({
          enabled_model_ids: updateData.enabledModelIds,
          data_source_preferences: updateData.dataSourcePreferences,
          country_preferences: updateData.countryPreferences,
          updated_at: new Date().toISOString(),
        })
        .eq('company_id', companyId);

      if (error) {
        throw new DataServiceError(
          `車両モデル設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseCompanyService: updateCompanyVehicleModelSettings完了`);
    } catch (error) {
      logger.error('❌ SupabaseCompanyService: updateCompanyVehicleModelSettings失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '車両モデル設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // ドライバー表示設定関連メソッド
  // =============================================================================

  /**
   * ドライバー陸送料金表示設定を取得
   * FL-229: ドライバーに陸送料金を表示するかどうかの設定
   * @param companyId 会社ID
   * @returns 表示設定
   */
  async getDriverTransportFeeVisibilitySettings(
    companyId: string
  ): Promise<DriverTransportFeeVisibilitySettings> {
    logger.info(
      `🚚 SupabaseCompanyService: getDriverTransportFeeVisibilitySettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      const company = await this.getCompanyById(companyId);
      if (!company) {
        logger.warn(`会社が見つかりません: ${companyId}`);
        return DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS;
      }

      // settings オブジェクトからドライバー表示設定を取得
      const settings = company.settings || {};
      const driverFeeSettings = settings.driverTransportFeeVisibility;

      if (!driverFeeSettings) {
        logger.info(`ドライバー表示設定が見つかりません、デフォルト値を返す`);
        return DEFAULT_DRIVER_TRANSPORT_FEE_VISIBILITY_SETTINGS;
      }

      logger.info(`✅ SupabaseCompanyService: getDriverTransportFeeVisibilitySettings完了`);
      return driverFeeSettings;
    } catch (error) {
      logger.error(
        '❌ SupabaseCompanyService: getDriverTransportFeeVisibilitySettings失敗:',
        error
      );
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ドライバー表示設定の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * ドライバー陸送料金表示設定を更新
   * FL-229: ドライバーに陸送料金を表示するかどうかの設定
   * @param companyId 会社ID
   * @param settings 新しい設定値
   */
  async updateDriverTransportFeeVisibilitySettings(
    companyId: string,
    settings: DriverTransportFeeVisibilitySettings
  ): Promise<void> {
    logger.info(
      `🔧 SupabaseCompanyService: updateDriverTransportFeeVisibilitySettings開始 - companyId: ${companyId}`
    );
    this.ensureSupabaseClient();

    try {
      // 現在の company データを取得
      const company = await this.getCompanyById(companyId);
      if (!company) {
        throw new DataServiceError(
          `会社が見つかりません: ${companyId}`,
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      // 既存の settings をマージ
      const updatedSettings = {
        ...(company.settings || {}),
        driverTransportFeeVisibility: settings,
      };

      // PostgreSQL の JSONB カラムに更新
      const { error } = await this.supabase!.from('companies')
        .update({
          settings: updatedSettings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', companyId);

      if (error) {
        throw new DataServiceError(
          `ドライバー表示設定の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      logger.info(`✅ SupabaseCompanyService: updateDriverTransportFeeVisibilitySettings完了`);
    } catch (error) {
      logger.error(
        '❌ SupabaseCompanyService: updateDriverTransportFeeVisibilitySettings失敗:',
        error
      );
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ドライバー表示設定の更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }
}

// Convenience singleton and function-style exports for app usage
export const supabaseCompanyService = new SupabaseCompanyService();

export const updateCompany = (
  companyId: string,
  updateData: Partial<Pick<Company, 'driverScheduleVisibility' | 'driverScheduleEditPermission'>>
) => supabaseCompanyService.updateCompany(companyId, updateData);

export const getCompanyById = (companyId: string) =>
  supabaseCompanyService.getCompanyById(companyId);

export const checkDriverScheduleEditPermission = (
  companyId: string,
  userId: string,
  transporterId?: string
) => supabaseCompanyService.checkDriverScheduleEditPermission(companyId, userId, transporterId);
